import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContentContext = createContext(null);

// Default content as fallback
const defaultContent = {
  global: {
    phone: "813-453-5988",
    email: "info@a-qualitypools.net",
    tagline: "A Cody Pools Company",
    serviceArea: "Central & Gulf Coast Florida",
    hours: "Mon-Fri: 8AM-6PM",
    facebook: "",
    instagram: "",
    twitter: ""
  },
  homepage: {
    heroTagline: "Florida's Premier Pool Service Company",
    heroHeadline: "Crystal Clear Pools,",
    heroHeadlineHighlight: "Every Time",
    heroSubtext: "Professional pool cleaning, maintenance, and repair services. Let us handle your pool so you can enjoy it stress-free.",
    servicesTitle: "Our Pool Services",
    servicesSubtitle: "From weekly maintenance to complete renovations, we've got all your pool needs covered.",
    whyUsTitle: "The A-Quality Difference",
    whyUsText: "As part of the Cody Pools family, we bring decades of experience and an unwavering commitment to quality. Your pool deserves the best care.",
    ctaHeadline: "Ready for a Crystal Clear Pool?",
    ctaSubtext: "Get a free quote today and see why Tampa Bay trusts A-Quality Pool Service.",
    testimonialsTitle: "What Our Customers Say",
    testimonialsSubtext: "Don't just take our word for it - hear from pool owners who trust A-Quality."
  },
  about: {
    heroTagline: "About Us",
    heroHeadline: "A Cody Pools Company",
    heroSubtext: "For over 15 years, A-Quality Pool Service has been keeping Tampa Bay pools crystal clear and swim-ready.",
    storyTitle: "Trusted Pool Service Since 2008",
    storyText1: "A-Quality Pool Service was founded with a simple mission: to provide Tampa Bay homeowners with reliable, professional pool care they can trust.",
    storyText2: "In 2016, we joined the Cody Pools family, combining our local expertise with the resources and standards of one of the industry's most trusted names.",
    storyText3: "Today, we proudly serve thousands of residential and commercial customers across the Tampa Bay area.",
    valuesTitle: "What We Stand For",
    valuesSubtext: "Our core values guide everything we do.",
    yearsInBusiness: "15+",
    yearsLabel: "Years of Experience",
    customersServed: "5,000+",
    customersLabel: "Happy Customers",
    satisfactionRate: "100%",
    satisfactionLabel: "Satisfaction Rate",
    supportHours: "24/7",
    supportLabel: "Customer Support",
    milestones: []
  },
  contact: {
    heroTagline: "Contact Us",
    heroHeadline: "Get Your Free Quote",
    heroSubtext: "Ready for crystal clear water? Contact us today for a free consultation.",
    formTitle: "Request a Quote",
    formSubtext: "Fill out the form below and we'll get back to you within 24 hours.",
    mapTitle: "Service Area",
    mapText: "We proudly serve Central & Gulf Coast Florida.",
    ctaHeadline: "Need Immediate Assistance?",
    ctaSubtext: "Call us now and speak directly with a pool care specialist."
  }
};

export const useContent = () => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
};

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState(defaultContent);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content`);
        if (response.data && Object.keys(response.data).length > 0) {
          setContent(prev => ({
            ...prev,
            ...response.data
          }));
        }
      } catch (error) {
        // Use default content on error
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  const refreshContent = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      if (response.data && Object.keys(response.data).length > 0) {
        setContent(prev => ({
          ...prev,
          ...response.data
        }));
      }
    } catch (error) {
      // Refresh failed — keep existing content
    }
  };

  const value = {
    content,
    loading,
    refreshContent,
    global: content.global || defaultContent.global,
    homepage: content.homepage || defaultContent.homepage,
    about: content.about || defaultContent.about,
    contact: content.contact || defaultContent.contact
  };

  return (
    <ContentContext.Provider value={value}>
      {children}
    </ContentContext.Provider>
  );
};
